import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageTitle } from 'common/components/PageTitle';
import { GroupForm } from 'user/components/GroupForm';
import {
    IContentTypeValue,
    IFeature,
    CreateGroupDocument,
    ExtraCategoriesDocument,
    UpdateExtraCategoryValuesDocument,
} from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';
import { useSnackbar } from 'common/hooks/useSnackbar';
import {
    getExtraCategoryInitialFormValues,
    getExtraCategoryValues,
} from 'organisation/utils/extraCategory';
import { useApolloError } from 'common/hooks/useApolloError';
import { Loader } from 'common/components/Loader';
import { useFeature } from 'common/hooks/useFeature';
import { TGroupCreateFormValues } from 'user/types';

export const GroupCreateDrawer = ({ open }: IPageDrawerProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { canUse: canUseSkills } = useFeature(IFeature.Skill);
    const {
        data: extraCategoryData,
        loading: extraCategoryLoading,
        error: extraCategoryError,
    } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.Group },
    });

    useEffect(() => {
        if (extraCategoryError) {
            showApolloError(extraCategoryError);
        }
    }, [extraCategoryError, showApolloError]);
    const [createGroup, { loading: createGroupLoading }] =
        useMutation(CreateGroupDocument);
    const [
        updateExtraCategoryValues,
        { loading: updateExtraCategoryValuesLoading },
    ] = useMutation(UpdateExtraCategoryValuesDocument);

    const { extraCategories } = extraCategoryData || {};

    const handleClose = () => {
        navigate(getUrl('GROUP_MANAGEMENT_LIST'));
    };

    const handleSubmit = async (values: TGroupCreateFormValues) => {
        const {
            maxUsers,
            extraCategories: extraCategoryCreateValues,
            participantIds: participants,
            managerIds: managers,
            trainingIds: trainings,
            skills,
            ...otherValues
        } = values;

        let groupId;
        const skillsInput = skills
            ?.filter((skillReq) => skillReq.skill?.id)
            .map((skillReq) => ({
                skillId: skillReq.skill!.id,
                minAbsolute: skillReq.minAbsolute
                    ? parseInt(String(skillReq.minAbsolute), 10)
                    : null,
                minPercent: skillReq.minPercent
                    ? parseInt(String(skillReq.minPercent), 10)
                    : null,
            }));

        try {
            const result = await createGroup({
                variables: {
                    group: {
                        participantIds: participants?.map(
                            (participant) => participant.id
                        ),
                        managerIds: managers?.map((manager) => manager.id),
                        trainingIds: trainings?.map((training) => training.id),
                        maxUsers: maxUsers ? +maxUsers : 0,
                        skills: skillsInput?.length ? skillsInput : undefined,
                        ...otherValues,
                    },
                    withSkills: canUseSkills,
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'paginatedGroups' });
                },
            });

            const extraCategoryValues = getExtraCategoryValues(
                extraCategoryCreateValues,
                extraCategories
            );

            const { group: newGroup } = result?.data?.createGroup || {};

            if (!newGroup) return;

            groupId = newGroup.id;

            // If extra categories are set update their values
            if (extraCategoryValues.length) {
                await updateExtraCategoryValues({
                    variables: {
                        contentType: IContentTypeValue.Group,
                        modelId: groupId,
                        values: extraCategoryValues,
                    },
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('groupsManagementPage.groupsActionSuccess.create'),
            {
                variant: 'success',
            }
        );

        handleClose();

        return;
    };

    const initialValues: TGroupCreateFormValues = {
        name: '',
        enableChat: false,
        ref: '',
        maxUsers: 0,
        extraCategories: {
            ...getExtraCategoryInitialFormValues(extraCategories, []),
        },
        skills: canUseSkills ? [] : undefined,
    };

    return (
        <PageDrawer open={open} onClose={handleClose}>
            <PageTitle>
                {`${translate('groupsManagementPage.pageTitle')} - ${translate(
                    'groupForm.createGroup'
                )}`}
            </PageTitle>

            <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 0, sm: 0 } }}>
                {extraCategoryLoading && <Loader />}

                {!extraCategoryLoading && (
                    <GroupForm
                        disabled={
                            createGroupLoading ||
                            updateExtraCategoryValuesLoading ||
                            extraCategoryLoading
                        }
                        initialValues={initialValues}
                        onClose={handleClose}
                        onSubmit={handleSubmit}
                    />
                )}
            </Box>
        </PageDrawer>
    );
};
